import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Card, Chip, Field } from '@promtver/ui-kit';

import {
  createDictionaryItem,
  createPromoBlock,
  deleteDictionaryItem,
  deletePromoBlock,
  getDictionary,
  getPromoBlocks,
  updateDictionaryItem,
} from '../../api/admin';
import { errorMessage } from '../../api/errors';
import { AppShell } from '../../components/AppShell';
import { AsyncState } from '../../components/AsyncState';
import { KeyValue } from '../../components/KeyValue';
import { ru } from '../../i18n/ru';
import type { DictionaryItem, PromoBlock } from '../../api/types';

type DictionaryKind = 'districts' | 'categories' | 'ticket-types' | 'safety-rules';

const TABS: Array<{ key: DictionaryKind | 'promo'; label: string }> = [
  { key: 'districts', label: ru.dictionaries.tabDistricts },
  { key: 'categories', label: ru.dictionaries.tabCategories },
  { key: 'ticket-types', label: ru.dictionaries.tabTicketTypes },
  { key: 'safety-rules', label: ru.dictionaries.tabSafety },
  { key: 'promo', label: ru.dictionaries.tabPromo },
];

/**
 * Управление справочниками (FR-ADM-03): районы, категории производств,
 * типы билетов, тексты требований ТБ и промо-блоки.
 */
export function DictionariesPage() {
  const [tab, setTab] = useState<DictionaryKind | 'promo'>('districts');

  return (
    <AppShell title={ru.dictionaries.title} subtitle={ru.dictionaries.subtitle}>
      <Card title={ru.dictionaries.title}>
        <div className="flex flex-wrap gap-2">
          {TABS.map((item) => (
            <Chip key={item.key} active={tab === item.key} onClick={() => setTab(item.key)}>
              {item.label}
            </Chip>
          ))}
        </div>
      </Card>

      {tab === 'promo' ? <PromoBlocks /> : <DictionaryList kind={tab} />}
    </AppShell>
  );
}

function DictionaryList({ kind }: { kind: DictionaryKind }) {
  const queryClient = useQueryClient();
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['admin', 'dictionary', kind],
    queryFn: () => getDictionary(kind),
  });

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ['admin', 'dictionary', kind] });
  };

  const createMutation = useMutation({
    mutationFn: () => createDictionaryItem(kind, { code: code.trim(), name: name.trim(), description: description.trim() }),
    onSuccess: async () => {
      setCode('');
      setName('');
      setDescription('');
      setError(null);
      await invalidate();
    },
    onError: (cause) => setError(errorMessage(cause, ru.common.unknownError)),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<DictionaryItem> }) =>
      updateDictionaryItem(kind, id, patch),
    onSuccess: invalidate,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteDictionaryItem(kind, id),
    onSuccess: invalidate,
  });

  const items = query.data ?? [];

  return (
    <Card title={ru.dictionaries.title}>
      <form
        className="flex flex-col gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          createMutation.mutate();
        }}
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={ru.dictionaries.code} value={code} onChange={setCode} required />
          <Field label={ru.dictionaries.name} value={name} onChange={setName} required />
        </div>
        <Field label={ru.dictionaries.description} value={description} onChange={setDescription} />
        {error ? <p className="text-xs text-danger">{error}</p> : null}
        <Button type="submit" tone="accent" disabled={createMutation.isPending || !code.trim() || !name.trim()}>
          {ru.dictionaries.add}
        </Button>
      </form>

      <AsyncState
        isLoading={query.isLoading}
        isError={query.isError}
        isEmpty={items.length === 0}
        emptyTitle={ru.dictionaries.empty}
        onRetry={() => void query.refetch()}
      >
        <ul className="flex flex-col gap-2">
          {items.map((item) => (
            <li key={item.id} className="rounded-card border border-line p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <strong className="text-sm">{item.name}</strong>
                <Badge tone={item.isActive ? 'success' : 'default'}>
                  {item.isActive ? ru.dictionaries.active : ru.dictionaries.inactive}
                </Badge>
              </div>
              <KeyValue label={ru.dictionaries.code} value={item.code} mono />
              {item.description ? (
                <KeyValue label={ru.dictionaries.description} value={item.description} />
              ) : null}
              <div className="mt-2 flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  onClick={() => updateMutation.mutate({ id: item.id, patch: { isActive: !item.isActive } })}
                >
                  {item.isActive ? ru.dictionaries.inactive : ru.dictionaries.active}
                </Button>
                <Button
                  variant="outline"
                  tone="danger"
                  disabled={deleteMutation.isPending}
                  onClick={() => deleteMutation.mutate(item.id)}
                >
                  {ru.common.delete}
                </Button>
              </div>
            </li>
          ))}
        </ul>
      </AsyncState>
    </Card>
  );
}

function PromoBlocks() {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [error, setError] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['admin', 'promo-blocks'],
    queryFn: getPromoBlocks,
  });

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ['admin', 'promo-blocks'] });
  };

  const createMutation = useMutation({
    mutationFn: () =>
      createPromoBlock({
        title: title.trim(),
        text: text.trim(),
        linkUrl: linkUrl.trim() || null,
        isActive: true,
        startsAt: null,
        endsAt: null,
      }),
    onSuccess: async () => {
      setTitle('');
      setText('');
      setLinkUrl('');
      setError(null);
      await invalidate();
    },
    onError: (cause) => setError(errorMessage(cause, ru.common.unknownError)),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deletePromoBlock(id),
    onSuccess: invalidate,
  });

  const blocks = query.data ?? [];

  return (
    <Card title={ru.dictionaries.tabPromo}>
      <form
        className="flex flex-col gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          createMutation.mutate();
        }}
      >
        <Field label={ru.dictionaries.blockTitle} value={title} onChange={setTitle} required />
        <Field label={ru.dictionaries.text} value={text} onChange={setText} />
        <Field label={ru.dictionaries.link} value={linkUrl} onChange={setLinkUrl} />
        {error ? <p className="text-xs text-danger">{error}</p> : null}
        <Button type="submit" tone="accent" disabled={createMutation.isPending || !title.trim()}>
          {ru.dictionaries.add}
        </Button>
      </form>

      <AsyncState
        isLoading={query.isLoading}
        isError={query.isError}
        isEmpty={blocks.length === 0}
        emptyTitle={ru.dictionaries.empty}
        onRetry={() => void query.refetch()}
      >
        <ul className="flex flex-col gap-2">
          {blocks.map((block: PromoBlock) => (
            <li key={block.id} className="rounded-card border border-line p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <strong className="text-sm">{block.title}</strong>
                <Badge tone={block.isActive ? 'success' : 'default'}>
                  {block.isActive ? ru.dictionaries.active : ru.dictionaries.inactive}
                </Badge>
              </div>
              {block.text ? <p className="mt-1 text-xs text-muted">{block.text}</p> : null}
              {block.linkUrl ? <KeyValue label={ru.dictionaries.link} value={block.linkUrl} mono /> : null}
              <div className="mt-2">
                <Button
                  variant="outline"
                  tone="danger"
                  disabled={deleteMutation.isPending}
                  onClick={() => deleteMutation.mutate(block.id)}
                >
                  {ru.common.delete}
                </Button>
              </div>
            </li>
          ))}
        </ul>
      </AsyncState>
    </Card>
  );
}
